import React, { useEffect, useState } from 'react';
import { Settings, Sparkles, AlertCircle, CheckCircle } from 'lucide-react';
import C from '../../constants/colors';
import * as api from '../../api/tenantSettings';

const MODEL_LABELS = {
  'claude-haiku-4-5-20251001': { tier: 'Included',   blurb: 'Fast & economical (default)',  color: C.textMuted },
  'claude-sonnet-4-6':         { tier: 'Premium',    blurb: 'Balanced quality',             color: C.blue },
  'claude-opus-4-7':           { tier: 'Premium+',   blurb: 'Highest quality, slower',      color: C.amber },
};

export default function HseAiSettingsView() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { kind: 'ok'|'err', text }

  async function refresh() {
    try { setSettings(await api.getHseSettings()); } catch (e) { console.error(e); }
  }
  useEffect(() => { refresh(); }, []);

  async function save(partial) {
    setSaving(true); setMessage(null);
    try {
      await api.updateHseSettings(partial);
      setMessage({ kind: 'ok', text: 'Saved' });
      await refresh();
    } catch (err) {
      setMessage({ kind: 'err', text: err?.response?.data?.error || err.message });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  }

  if (!settings) return <div style={{ padding: 24, color: C.textMuted }}>Loading…</div>;

  return (
    <div style={{ padding: 24, color: C.textPrimary, maxWidth: 640 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Settings size={22} color={C.blue} />
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>HSE AI Settings</h2>
      </div>

      {message && (
        <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 16, fontSize: 12,
          background: (message.kind === 'ok' ? C.green : C.red) + '22',
          color: message.kind === 'ok' ? C.green : C.red,
          display: 'flex', alignItems: 'center', gap: 6 }}>
          {message.kind === 'ok' ? <CheckCircle size={12} /> : <AlertCircle size={12} />} {message.text}
        </div>
      )}

      {/* Synthesis toggle */}
      <Section title="AI Summary on Search Results">
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={settings.synthesisEnabled}
            disabled={saving}
            onChange={e => save({ synthesisEnabled: e.target.checked })} />
          <span><Sparkles size={12} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4, color: C.amber }} />
            Enable AI-generated summary on repository searches</span>
        </label>
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6, marginLeft: 22 }}>
          When on, repository search results include a 3-6 sentence AI summary with citations.
          Premium feature — usage billed per search (~$0.003–$0.05 depending on model tier below).
        </div>
      </Section>

      {/* Model picker */}
      <Section title="AI Model">
        {settings.allowedModels.map(m => {
          const label = MODEL_LABELS[m] || { tier: 'Unknown', blurb: '', color: C.textMuted };
          return (
            <label key={m} style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: 10, marginBottom: 6, borderRadius: 8,
              border: `1px solid ${settings.llmModel === m ? C.blue : C.border}`,
              background: settings.llmModel === m ? C.blue + '11' : 'transparent',
              cursor: 'pointer' }}>
              <input type="radio" name="model" value={m} checked={settings.llmModel === m}
                disabled={saving}
                onChange={() => save({ llmModel: m })}
                style={{ marginTop: 3 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {m}
                  <span style={{ marginLeft: 8, fontSize: 10, color: label.color, fontWeight: 700,
                    textTransform: 'uppercase' }}>{label.tier}</span>
                </div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{label.blurb}</div>
              </div>
            </label>
          );
        })}
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>
          Applies to AI summary on search results AND to tag suggestion when promoting findings to the Knowledge Base.
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase',
        letterSpacing: '0.06em', marginBottom: 10 }}>{title}</h3>
      {children}
    </div>
  );
}
